import React, { useMemo } from 'react';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { useAppSelector } from 'shared/store';
import { AvatarStack } from 'browser/components/AvatarStack';
import { SourceType } from 'types/outbound';
import { SpotlightActivity, SpotlightActivityCallProps } from '../ActivityGroup';
import { StatusBadge } from 'browser/components/Outbound/CallTaskDisplay';
import { formatSecondsToTimer } from 'shared/utils/helpers';
import {
  getActivityCallDisposition,
  getActivityContactList,
  getActivityIcon,
} from 'shared/utils/outbound';

export const SpotlightActivityCallItem: React.FC<
  {
    activity: SpotlightActivity;
  } & SpotlightActivityCallProps
> = ({
  activity,
  apolloCallDispositions,
  apolloContactLists,
  hubspotCallDispositions,
  hubspotContactLists,
  outreachCallDispositions,
  outreachContactLists,
  salesforceCallDispositions,
  salesforceContactLists,
  salesloftCallDispositions,
  salesloftContactLists,
  servicebellCallDispositions,
  servicebellContactLists,
}) => {
  const users = useAppSelector(selectActiveOrgMembers);
  const user = useMemo(() => {
    return users.find(user => user.id === activity.userId);
  }, [activity, users]);

  return (
    <div className="SpotlightActivityCallItem">
      <div className="SpotlightActivityCallItem__icon">
        <Icon icon="phoneRingingOutline" width="20" height="20" />
      </div>
      <div className="SpotlightActivityCallItem__content">
        <div className="SpotlightActivityCallItem__summary">
          <div className="SpotlightActivityCallItem__summaryWrap">
            Called by
            <div className="SpotlightActivityCallItem__agent">
              <AvatarStack users={[user ?? []].flat()} overlap="tight" />
              {user?.displayName ?? 'Unknown'}
            </div>
          </div>
          <div className="SpotlightActivityCallItem__duration">
            {!!activity?.data?.dialTask?.callDuration &&
              formatSecondsToTimer(activity?.data?.dialTask?.callDuration)}
          </div>
        </div>
        <div className="SpotlightActivityCallItem__details">
          <div className="SpotlightActivityCallItem__row">
            <div className="SpotlightActivityCallItem__value">
              <div className="SpotlightActivityCallItem__label">Disposition</div>
              <StatusBadge
                field={getActivityCallDisposition(
                  {
                    externalServiceDispositionId:
                      activity.data.dialTask.externalServiceDispositionId,
                    dialTask: activity.data.dialTask,
                  },
                  apolloCallDispositions,
                  hubspotCallDispositions,
                  outreachCallDispositions,
                  salesforceCallDispositions,
                  salesloftCallDispositions,
                  servicebellCallDispositions,
                )}
              />
            </div>
            <div className="SpotlightActivityCallItem__value">
              <div className="SpotlightActivityCallItem__label">Dial List</div>
              <span>
                {activity?.data?.dialTask?.sourceType !== SourceType.DIALPAD && (
                  <>
                    <Icon width={16} height={16} icon={getActivityIcon(activity.data)} />
                    <span>
                      {getActivityContactList(
                        activity.data,
                        apolloContactLists,
                        hubspotContactLists,
                        outreachContactLists,
                        salesforceContactLists,
                        salesloftContactLists,
                        servicebellContactLists,
                      )}
                    </span>
                  </>
                )}
                {activity?.data?.dialTask?.sourceType === SourceType.DIALPAD && 'Dialpad'}
              </span>
            </div>
          </div>
          <div className="SpotlightActivityCallItem__row">
            <div className="SpotlightActivityCallItem__value">
              <div className="SpotlightActivityCallItem__label">Call Notes</div>
              {activity?.data?.notes}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
